import React from 'react'
import { omit } from '@chakra-ui/utils'
import { mergeClassnames } from '@mote/helpers'
import { __DEV__ } from '@mote/helpers/constants'
import { x } from '@xstyled/styled-components'
import type { HTMLXStyledProps } from '../../system'
import { forwardRef } from '../../system'
import type { FallbackStrategy, UseImageProps } from './useImage'
import { shouldShowFallbackImage, useImage } from './useImage'

export interface ImageProps
  extends UseImageProps,
    Omit<HTMLXStyledProps<'img'>, keyof UseImageProps> {
  htmlWidth?: string | number
  htmlHeight?: string | number
  fallbackSrc?: string
  fallback?: React.ReactElement
  loading?: 'eager' | 'lazy'
  fit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down'
  align?: 'top' | 'bottom' | 'left' | 'right' | 'center'
  ignoreFallback?: boolean
  fallbackStrategy?: FallbackStrategy
  referrerPolicy?: React.HTMLAttributeReferrerPolicy
}

/**
 * React component that renders an image with support
 * for fallbacks
 */
export const Image = forwardRef<ImageProps, 'img'>((props, ref) => {
  const {
    fallbackSrc,
    fallback,
    src,
    srcSet,
    align,
    fit,
    loading,
    ignoreFallback,
    crossOrigin,
    fallbackStrategy = 'beforeLoadOrError',
    referrerPolicy,
    htmlWidth,
    htmlHeight,
    alt,
    className,
    ...rest
  } = props

  const classnames = {
    placeholder: mergeClassnames('mote-image__placeholder', className),
    image: mergeClassnames('mote-image', className)
  }
  const providedFallback = fallbackSrc !== undefined || fallback !== undefined
  const shouldIgnoreFallbackImage =
    loading != null || ignoreFallback || !providedFallback

  const status = useImage({
    ...props,
    ignoreFallback: shouldIgnoreFallbackImage
  })

  const showFallbackImage = shouldShowFallbackImage(status, fallbackStrategy)

  const shared = {
    ref,
    alt,
    objectFit: fit,
    objectPosition: align,
    width: htmlWidth,
    height: htmlHeight,
    ...(shouldIgnoreFallbackImage ? rest : omit(rest, ['onError', 'onLoad']))
  }

  if (showFallbackImage) {
    if (fallback) return fallback

    return (
      <x.img className={classnames.placeholder} src={fallbackSrc} {...shared} />
    )
  }

  return (
    <x.img
      src={src}
      srcSet={srcSet}
      crossOrigin={crossOrigin}
      loading={loading}
      referrerPolicy={referrerPolicy}
      className={classnames.image}
      {...shared}
    />
  )
})

if (__DEV__) {
  Image.displayName = 'Image'
}
